/**
 * First-run activation checklist for the dashboard. Guides a new user to their
 * "aha" moment (a first successful agent run) and hides itself once activated.
 *
 * The fastest path — one click — creates an active starter agent that drafts a
 * reply to a sample support ticket, kicks off a run, and lands the user on the
 * run page to watch it succeed and see what it cost.
 */
import { Link, useNavigate } from 'react-router-dom';
import {
  useOnboarding,
  useCreateStarterAgent,
} from '../../hooks/useOnboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

export function GettingStarted() {
  const { data, isLoading } = useOnboarding();
  const navigate = useNavigate();
  const createStarter = useCreateStarterAgent();

  // Nothing to show while loading, on error, or once the user has activated.
  if (isLoading || !data || data.activated) return null;

  function handleStarter() {
    createStarter.mutate(undefined, {
      onSuccess: (res) => navigate(`/executions/${res.executionId}`),
    });
  }

  const steps = [
    { done: data.createdAgent, label: 'Create your first agent' },
    { done: data.ranAgent, label: 'Activate it and run it' },
    { done: data.activated, label: 'See a successful run' },
  ];
  const next = steps.find((s) => !s.done);

  return (
    <Card className="mb-8 border-primary/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          🚀 Get started — 3 steps to your first working agent
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data.createdAgent && (
          <p className="text-sm text-muted-foreground">
            Try it on a real job first: an agent drafts a reply to a sample support
            ticket. It takes a few seconds, and you see the reply, every step, and
            exactly what the run cost.
          </p>
        )}
        <ul className="space-y-2">
          {steps.map((s) => (
            <li key={s.label} className="flex items-center gap-2 text-sm">
              <span
                className={s.done ? 'text-green-600' : 'text-muted-foreground'}
                aria-hidden
              >
                {s.done ? '✓' : '○'}
              </span>
              <span className={s.done ? 'text-muted-foreground line-through' : ''}>
                {s.label}
              </span>
            </li>
          ))}
        </ul>

        {!data.createdAgent ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleStarter}
              disabled={createStarter.isPending}
              className="h-auto min-h-10 whitespace-normal py-2 text-left"
            >
              {createStarter.isPending
                ? 'Creating…'
                : '✨ Draft a support reply with a starter agent'}
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/agents/new">or build your own</Link>
            </Button>
          </div>
        ) : (
          !data.ranAgent && (
            <Button asChild size="sm">
              <Link to="/agents">Go to agents</Link>
            </Button>
          )
        )}
        {next?.label === 'See a successful run' && data.ranAgent && (
          <Button asChild size="sm" variant="outline">
            <Link to="/executions">View your runs</Link>
          </Button>
        )}
        {createStarter.isError && (
          <p className="text-sm text-red-600">
            Couldn&apos;t create the starter agent. Please try again.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          New to AgentMesh?{' '}
          <Link to="/help/getting-started" className="underline">
            Read the 3-step guide
          </Link>
          .
        </p>
      </CardContent>
    </Card>
  );
}
